import React, { useLayoutEffect } from "react";
import PropTypes from "prop-types";

// Imports unitaires (jamais l'index du design system) : ce module est tiré
// par le bundle principal pour peindre la V2 dès le premier écran.
import { ThemeAProvider } from "../../design-system/v2/foundations/ThemeAProvider";
import MediaImage from "../../design-system/v2/components/data-display/MediaImage";
import { Skeleton, SkeletonLayout } from "../../design-system/v2/components/feedback/Skeleton";
import Page from "../../design-system/v2/components/layout/Page";
import Stack from "../../design-system/v2/components/layout/Stack";
import { bootThemeVariant } from "./bootTheme";
import ShellIcon from "./icons";
import { readShellPreferences } from "./shellPreferences";

/**
 * Variante de squelette de contenu selon l'adresse V2 (partagée avec
 * WelfareRouteLoader, qui prend le relais à l'intérieur du shell).
 */
export const resolveRouteSkeletonVariant = (pathname = "") => {
  const analysis = pathname.match(/^\/v2\/analysis\/(scan|focal|questionnaire|enrichment)(?:\/(result))?\/?$/);
  if (analysis) return analysis[2] ? `analysis-${analysis[1]}` : "table";
  if (/^\/v2\/analysis\//.test(pathname)) return "analysis";
  if (/^\/v2\/veterinary(?:\/|$)/.test(pathname)) return "veterinary";
  if (/^\/v2\/sectors\//.test(pathname)) return "sector";
  if (/^\/v2\/tours(?:\/|$)/.test(pathname)) return "tour";
  if (/^\/v2\/enclosures\//.test(pathname)) return "enclosure";
  if (/^\/v2\/individuals\//.test(pathname)) return "individual";
  if (/^\/v2\/groups\//.test(pathname)) return "group";
  if (/^\/v2\/campaigns(?:\/|$)/.test(pathname)) return "campaign";
  if (/^\/v2\/(?:custom-parameters|groups|individuals|enclosures|sectors)(?:\/|$)/.test(pathname)) return "table";
  return "content";
};

const MENU_SKELETON_ROWS = Object.freeze([
  { icon: "dashboard", width: "akv:w-[68%]" },
  { icon: "tasks", width: "akv:w-[82%]" },
  { icon: "animals", width: "akv:w-[61%]" },
  { icon: "timeline", width: "akv:w-[74%]" },
]);

function SidebarBootstrapSkeleton({ compact = false }) {
  const labelVisibility = `akv:min-[701px]:max-[940px]:hidden ${compact ? "akv:hidden!" : ""}`;

  return (
    <aside
      aria-hidden="true"
      className="akv:sticky akv:top-0 akv:hidden akv:h-screen akv:min-h-0 akv:flex-col akv:overflow-hidden akv:bg-ak-inverse akv:shadow-[6px_0_18px_rgba(23,60,50,0.06)] akv:min-[701px]:flex akv:[--ak-v2-skeleton-shimmer:rgba(255,255,255,0.06)]"
      data-ak-v2-component="desktop-navigation-skeleton"
    >
      <div className={`akv:flex akv:min-h-16 akv:items-center akv:gap-3 akv:px-2 akv:py-3 ${compact ? "akv:min-[941px]:justify-center" : "akv:min-[941px]:px-[1.375rem]"}`}>
        <span className="akv:grid akv:h-9 akv:w-9 akv:flex-none akv:place-items-center akv:overflow-hidden akv:rounded-ak-control akv:bg-white akv:p-1">
          <MediaImage alt="" fit="contain" loading="eager" src="/img/logo_akongo.png" />
        </span>
        <div className={`akv:min-w-0 akv:flex-1 akv:space-y-2 ${labelVisibility}`}>
          <Skeleton className="akv:h-2.5 akv:w-[72%] akv:bg-ak-surface/20" variant="custom" />
          <Skeleton className="akv:h-2 akv:w-[56%] akv:bg-ak-surface/10" variant="custom" />
        </div>
        <Skeleton className={`akv:h-7 akv:w-7 akv:rounded-ak-control akv:bg-ak-surface/[0.08] ${labelVisibility}`} variant="custom" />
      </div>

      <div className={`akv:flex-1 akv:overflow-hidden akv:px-2 akv:py-5 ${compact ? "" : "akv:min-[941px]:px-3"}`}>
        <div className="akv:space-y-2" data-skeleton-zone="navigation-sections">
          <div>
            <div className="akv:flex akv:min-h-8 akv:items-center akv:gap-2 akv:px-2 akv:py-1">
              <Skeleton className={`akv:h-2 akv:w-[52%] akv:bg-ak-surface/14 ${labelVisibility}`} variant="custom" />
              <Skeleton className={`akv:ml-auto akv:h-4 akv:w-4 akv:rounded-full akv:bg-ak-surface/[0.08] ${labelVisibility}`} variant="custom" />
            </div>
            <div className="akv:space-y-1 akv:pt-1">
              {MENU_SKELETON_ROWS.map((row, index) => (
                <div
                  className={`akv:relative akv:flex akv:min-h-10 akv:items-center akv:gap-2 akv:overflow-hidden akv:rounded-ak-control akv:px-2 akv:py-1.5 ${index === 0 ? "akv:bg-ak-surface/[0.055] akv:before:absolute akv:before:left-0 akv:before:top-1/2 akv:before:h-5 akv:before:w-0.5 akv:before:-translate-y-1/2 akv:before:rounded-full akv:before:bg-ak-highlight/70" : ""}`}
                  data-skeleton-zone="navigation-row"
                  key={row.icon}
                >
                  <span className="akv:grid akv:h-7 akv:w-7 akv:flex-none akv:place-items-center akv:text-ak-surface/24">
                    <ShellIcon name={row.icon} />
                  </span>
                  <Skeleton className={`akv:h-2.5 akv:bg-ak-surface/14 ${row.width} ${labelVisibility}`} variant="custom" />
                </div>
              ))}
            </div>
          </div>

          {["akv:w-[64%]", "akv:w-[48%]", "akv:w-[58%]"].map((width, index) => (
            <div className="akv:flex akv:min-h-8 akv:items-center akv:gap-2 akv:px-2 akv:py-1" key={width}>
              <Skeleton className={`akv:h-2 akv:bg-ak-surface/10 ${width} ${labelVisibility}`} variant="custom" />
              <Skeleton className={`akv:ml-auto akv:h-4 akv:w-4 akv:rounded-full akv:bg-ak-surface/[0.06] ${labelVisibility}`} variant="custom" />
              <span className={`akv:hidden akv:h-1.5 akv:w-1.5 akv:rounded-full akv:bg-ak-surface/20 ${compact ? "akv:block!" : "akv:min-[701px]:max-[940px]:block"}`} />
            </div>
          ))}
        </div>
      </div>

      {!compact ? (
        <div className="akv:flex-none akv:px-3 akv:pb-4 akv:min-[701px]:max-[940px]:hidden" data-skeleton-zone="version-switch">
          <div className="akv:rounded-ak-control akv:bg-ak-surface/[0.07] akv:p-1">
            <div className="akv:grid akv:grid-cols-2 akv:gap-1">
              <Skeleton className="akv:h-9 akv:rounded-ak-control akv:bg-ak-surface/16" variant="custom" />
              <Skeleton className="akv:h-9 akv:rounded-ak-control akv:bg-ak-surface/[0.07]" variant="custom" />
            </div>
          </div>
        </div>
      ) : null}
    </aside>
  );
}

SidebarBootstrapSkeleton.propTypes = { compact: PropTypes.bool };

export function ShellBootstrapSkeleton({ children, headerActions = null }) {
  // Le squelette reprend la largeur de barre persistée (repli « L2
  // rétractable ») pour éviter un saut de grille quand le vrai shell monte.
  const navigationCollapsed = readShellPreferences().navigationCollapsed;
  return (
    <ThemeAProvider>
      <div
        className={`akv:grid akv:min-h-screen akv:bg-ak-subtle akv:bg-[linear-gradient(180deg,var(--ak-v2-surface-canvas-tint),var(--ak-v2-surface-subtle)_21.25rem)] akv:bg-no-repeat akv:min-[701px]:grid-cols-[4.75rem_minmax(0,1fr)] ${navigationCollapsed ? "akv:min-[941px]:grid-cols-[4.75rem_minmax(0,1fr)]" : "akv:min-[941px]:grid-cols-[15.5rem_minmax(0,1fr)]"}`}
        data-ak-v2-component="welfare-shell-bootstrap"
      >
        <SidebarBootstrapSkeleton compact={navigationCollapsed} />
        <div className="akv:min-w-0">
          <div className="akv:sticky akv:top-0 akv:z-40 akv:h-16 akv:bg-ak-surface/95 akv:shadow-ak-surface">
            <div className="akv:flex akv:h-full akv:items-center akv:justify-between akv:gap-5 akv:px-4 akv:md:px-6">
              <Skeleton className="akv:h-9 akv:w-full akv:max-w-md akv:rounded-ak-control" variant="custom" />
              {/* Les actions par défaut du header (thème, langue, confidentialité…)
                  sont autonomes : montées dès le bootstrap, elles n'attendent ni la
                  route ni ses données — le sélecteur de thème est disponible
                  immédiatement. (Sous jest, le chunk réveillerait un import
                  circulaire des connecteurs V1 : on garde le squelette.) */}
              {headerActions || <Skeleton variant="button" />}
            </div>
          </div>
          <main className="akv:min-w-0 akv:px-4 akv:pb-8 akv:md:px-6">
            {children}
          </main>
        </div>
      </div>
    </ThemeAProvider>
  );
}

ShellBootstrapSkeleton.propTypes = { children: PropTypes.node.isRequired, headerActions: PropTypes.node };

/** Contenu d'attente d'une page V2 : même cadre que WelfareRouteLoader. */
export function RouteContentSkeleton({ variant = "auto" }) {
  const resolved = variant === "auto"
    ? resolveRouteSkeletonVariant(typeof window === "undefined" ? "" : window.location.pathname)
    : variant;
  return (
    <Page width="full">
      <Stack gap="page">
        <div aria-busy="true" className="akv:min-h-[24rem] akv:[&_[data-skeleton-zone='header']]:hidden" data-ak-v2-component="welfare-route-loader">
          <SkeletonLayout variant={resolved} />
        </div>
      </Stack>
    </Page>
  );
}

RouteContentSkeleton.propTypes = { variant: PropTypes.string };

/**
 * Premier écran d'une adresse V2, rendu depuis le bundle principal pendant que
 * le chunk du shell se télécharge : le cadre du shell en squelette, dans le
 * thème et à l'échelle choisis — jamais le chargeur de l'interface Classique.
 */
export function WelfareBootScreen() {
  // Avant la première peinture : thème et échelle stockés sur <html>.
  useLayoutEffect(() => { bootThemeVariant(); }, []);
  return (
    <ShellBootstrapSkeleton>
      <RouteContentSkeleton />
    </ShellBootstrapSkeleton>
  );
}
